import _ from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { Base } from './base';
import { Size as NumberSize, Range as NumberRange } from './number';
import { IValidationRule } from '../types';

/**
 * Define a validator to check whether the input string can be converted to a valid number
 * @param {I18nModel} i18n - Provides a callback getString; will be called with args 'Validation_Number_Require_Numeric'
 */
class Type extends Base implements IValidationRule {
  message: string;

  i18n: I18nModel;

  constructor(i18n: I18nModel) {
    super(i18n);

    this.message = i18n.getString('Validation_Number_Require_Numeric');
  }

  isValidValue(value: any): boolean {
    return (_.isString(value) && _.isEmpty(value)) || this.i18n.isValidDecimal(value, true);
  }
}

/**
 * Define a validator to check the size of a string representation of a number
 * @param {number} integerLength - The length of the integer part
 * @param {number} decimalLength - The length of the decimal part
 * @param {I18nModel} i18n - Used for numeric formatting in number/Size
 */
class Size extends NumberSize {
  i18n: I18nModel;

  isValidValue(value: any): boolean {
    if (_.isString(value) && _.isEmpty(value)) {
      return true;
    }

    if (!this.i18n.isValidDecimal(value, true)) {
      return false;
    }

    const number = this.i18n.parseDecimal(value);

    if (putil.isNil(number)) {
      return false;
    }

    return super.isValidValue(number);
  }
}

/**
 * Define a validator to check the range of a string number
 * @param {number} min - The minimum value of the input
 * @param {number} max - The maximum value of the input
 * @param {I18nModel} i18n - Used for numeric formatting in number/Range
 */
class Range extends NumberRange {
  i18n: I18nModel;

  isValidValue(value: any): boolean {
    if (_.isString(value) && _.isEmpty(value)) {
      return true;
    }

    if (!this.i18n.isValidDecimal(value, true)) {
      return false;
    }

    return super.isValidValue(this.i18n.parseDecimal(value));
  }
}

/**
 * Create a string number type validator instance
 * @param {I18nModel} i18n - Provides a callback getString to retrieve an error message
 * @returns {IValidationRule} - String number type validator
 */
export function type(i18n: I18nModel): IValidationRule {
  return new Type(i18n);
}

/**
 * Create a string number size validator instance
 * @param {number} integerLength - The length of the integer part
 * @param {number} decimalLength - The length of the decimal part
 * @param {I18nModel} i18n - Used for numeric formatting in number/Size
 * @returns {IValidationRule} - String number size validator
 */
export function size(integerLength: number, decimalLength: number, i18n: I18nModel): IValidationRule {
  return new Size(integerLength, decimalLength, i18n);
}

/**
 * Create a string number range validator instance
 * @param {number} min - The minimum value of the input
 * @param {number} max - The maximum value of the input
 * @param {I18nModel} i18n - Used for numeric formatting in number/Range
 * @returns {IValidationRule} - String number range validator
 */
export function range(min: number, max: number, i18n: I18nModel): IValidationRule {
  return new Range(min, max, i18n);
}
